"use client";

import React, { useEffect, useRef } from "react";
import { useInView, motion, AnimatePresence } from "motion/react";
import { TextRotate, TextRotateRef } from "src/view/components/TextRotate";
import { Typography, Box } from "@mui/material";
import { ShowChart, AccountBalance, SettingsVoice, Lock, Hub, VerifiedUser } from "@mui/icons-material";
import Img01 from 'src/resources/images/01.png';
import Img02 from 'src/resources/images/02.png';
import Img03 from 'src/resources/images/03.png';

const COLORS = {
  primaryGreen: '#00f296',
  textDim: '#a0b0a8',
  textMain: '#ffffff',
};

const features = [
  {
    author: "01 — The Nature Atlas",
    url: Img01,
    title: "The Nature Atlas",
    description: "Every hectare tells a story — our platform reads it. Multi-spectral satellite imagery fused with ground-level bioacoustic sensors delivers deep-tier monitoring of species return rates, ecosystem health, and land condition, processing terabytes of telemetry daily.",
    items: [
      { icon: <ShowChart />, text: "Nature Metrics (biomass, soil quality)" },
      { icon: <Hub />, text: "Ecological Insights (species distribution)" }
    ]
  },
  {
    author: "02 — Listening to Ecosystems",
    url: Img03,
    title: "Listening to Ecosystems",
    description: "Nature speaks volumes — our models are built to listen. By fusing bioacoustics with environmental DNA, Darukaa detects species-level shifts long before they'd surface in visual or satellite data.",
    items: [
      { icon: <SettingsVoice />, text: "Audio Analyzer" },
      { icon: <Lock />, text: "eDNA (Coming Soon)" }
    ]
  },
  {
    author: "03 — Pricing the Risk",
    url: Img02,
    title: "Pricing the Risk",
    description: "Turning nature risk into financial insight. Darukaa standardizes environmental disclosure with automated, audit-ready reporting — built to TNFD and TCFD frameworks, with real-time physical risk scoring you can act on.",
    items: [
      { icon: <AccountBalance />, text: "TNFD Analytics" },
      { icon: <VerifiedUser />, text: "TCFD Compliance" }
    ]
  }
];

function ShowcaseItem({
  index,
  feature,
  onInView,
}: {
  index: number;
  feature: typeof features[0];
  onInView: (inView: boolean, index: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    margin: "-45% 0px -45% 0px",
  });

  useEffect(() => {
    onInView(isInView, index);
  }, [isInView, onInView, index]);

  return (
    <Box
      ref={ref}
      key={index}
      sx={{
        height: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        scrollSnapAlign: "center",
      }}
    >
      <Box sx={{ 
        width: "95%", 
        height: "85%", 
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "16px",
        overflow: "hidden",
      }}>
        <img
          src={feature.url}
          alt={feature.title}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            borderRadius: "16px",
            opacity: 1,
            filter: "contrast(1.1) brightness(1.1)",
          }}
        />
      </Box>
    </Box>
  );
}

export function FeatureShowcaseScroll() {
  const textRotateRef = useRef<TextRotateRef>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const handleInView = React.useCallback((inView: boolean, index: number) => {
    if (inView) {
      setActiveIndex(index);
      if (textRotateRef.current) {
        textRotateRef.current.jumpTo(index);
      }
    }
  }, []);

  return (
    <Box sx={{ width: "100%", height: "100vh", display: "flex", bgcolor: "#000", position: "relative", overflow: "hidden" }}>
      <Box
        sx={{
          width: "45%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: "5%",
          background: "linear-gradient(to left, rgba(0,0,0,0.8), #000)",
          zIndex: 20,
        }}
      >
        <TextRotate
          ref={textRotateRef}
          texts={features.map((f) => f.title)}
          mainClassName="font-bold text-white mb-4"
          style={{ fontSize: '2.5rem', lineHeight: '1.2' }}
          splitLevelClassName="overflow-hidden pb-1"
          staggerFrom={"first"}
          animatePresenceMode="wait"
          loop={false}
          auto={false}
          staggerDuration={0.02}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{ type: "spring", duration: 0.6, bounce: 0 }}
        />

        <Box sx={{ mt: 2 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
            >
              <Typography
                sx={{
                  color: COLORS.textDim,
                  fontSize: "1rem",
                  maxWidth: "450px",
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                {features[activeIndex].description}
              </Typography>

              <Box component="ul" sx={{ listStyle: "none", p: 0 }}>
                {features[activeIndex].items.map((item, i) => (
                  <Box
                    key={i}
                    component="li"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                      mb: "15px",
                      fontWeight: 600,
                      color: "#fff",
                    }}
                  >
                    {React.cloneElement(item.icon as React.ReactElement, {
                      sx: { color: COLORS.primaryGreen, fontSize: "1.2rem" },
                    })}
                    {item.text}
                  </Box>
                ))}
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>

      <Box sx={{ flex: 1, position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflowY: "auto",
            scrollSnapType: "y mandatory",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {features.map((feature, index) => (
            <ShowcaseItem
              key={index}
              index={index}
              feature={feature}
              onInView={handleInView}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
